import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import {
  Stethoscope, Users, DollarSign, TrendingUp, Plus, Share2, Loader2, Layers,
} from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString()}`;

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-border rounded-xl p-6 hover:shadow-[0_6px_24px_rgba(15,23,42,0.06)] transition-shadow">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center mb-4 ${accent ? "bg-patriot/10" : "bg-navy/5"}`}>
        <Icon className={`h-5 w-5 ${accent ? "text-patriot" : "text-navy"}`} />
      </div>
      <p className="font-display text-3xl font-bold text-navy">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const levelData = Object.entries(stats.earnings_by_level || {}).map(([lvl, amt]) => ({
    name: `L${lvl}`, amount: amt,
  }));

  return (
    <div data-testid="overview-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Dashboard</p>
          <h1 className="font-display text-3xl text-navy mt-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate("/dashboard/physicians")} data-testid="overview-add-physician" className="rounded-full bg-navy hover:bg-navy-dark text-white gap-2">
            <Plus className="h-4 w-4" /> Onboard Physician
          </Button>
          <Button onClick={() => navigate("/dashboard/share")} variant="outline" data-testid="overview-share" className="rounded-full border-navy text-navy hover:bg-navy hover:text-white gap-2">
            <Share2 className="h-4 w-4" /> Share
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={DollarSign} label="Total earnings" value={fmt(stats.total_earnings)} accent />
        <StatCard icon={TrendingUp} label="Personal sales income" value={fmt(stats.personal_earnings)} />
        <StatCard icon={Layers} label="Override income" value={fmt(stats.override_earnings)} />
        <StatCard icon={Stethoscope} label="Physicians you onboarded" value={stats.personal_physicians} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-6">
          <h2 className="font-display text-xl text-navy mb-1">Overrides by level</h2>
          <p className="text-sm text-muted-foreground mb-6">How your override earnings break down across your six-level network.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={levelData}>
                <XAxis dataKey="name" tickLine={false} axisLine={false} stroke="#475569" />
                <YAxis tickLine={false} axisLine={false} stroke="#475569" tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: "rgba(27,58,107,0.05)" }} />
                <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                  {levelData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#C0392B" : "#1B3A6B"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-6">
          <h2 className="font-display text-xl text-navy mb-4">Team by level</h2>
          <div className="space-y-3">
            {Object.entries(stats.team_by_level || {}).map(([lvl, count]) => (
              <div key={lvl} className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Level {lvl}</span>
                <div className="flex items-center gap-3 flex-1 mx-4">
                  <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-navy rounded-full transition-all" style={{ width: `${Math.min(count * 25, 100)}%` }} />
                  </div>
                </div>
                <span className="font-semibold text-navy w-6 text-right">{count}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Direct recruits</span>
            <span className="font-display text-2xl text-navy">{stats.direct_recruits}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
